import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import {
  Box, Typography, Tabs, Tab, Paper, Chip, Card, CardContent,
  CardActionArea, Grid, Stack, Alert,
} from '@mui/material';
import {
  Extension as ExtensionIcon,
  Inventory2 as ToolbarIcon,
  OpenInNew as PopupIcon,
  ErrorOutline as ErrorStateIcon,
  NotificationsActive as BgRunIcon,
} from '@mui/icons-material';
import ExtV5ToolbarIconMockup from './ExtV5ToolbarIconMockup';
import ExtV5PopupMockup from './ExtV5PopupMockup';
import ExtV5ErrorStateMockup from './ExtV5ErrorStateMockup';
import ExtV5BackgroundRunMockup from './ExtV5BackgroundRunMockup';

const PURPLE = '#7b1fa2';

const MOCKS = [
  {
    key: 'toolbar',
    label: 'Toolbar icon',
    icon: <ToolbarIcon />,
    title: 'Toolbar icon',
    summary: 'Pinned Chrome icon — normal vs. disconnected (red-dot overlay). The entire visible surface most of the time.',
    component: ExtV5ToolbarIconMockup,
  },
  {
    key: 'popup',
    label: 'Popup',
    icon: <PopupIcon />,
    title: 'Popup · happy path',
    summary: '300×200 popup: logo + version, Connected dot, optional last-sign-in line, View details link. No script list.',
    component: ExtV5PopupMockup,
  },
  {
    key: 'errors',
    label: 'Error states',
    icon: <ErrorStateIcon />,
    title: 'Popup · degraded states',
    summary: 'Offline, out-of-date, unauthenticated. Same 300×200 popup with one corrective action each.',
    component: ExtV5ErrorStateMockup,
  },
  {
    key: 'background-run',
    label: 'Background run',
    icon: <BgRunIcon />,
    title: 'Background run',
    summary: 'What the user sees when a script runs: a small toast at bottom-right for ~4 seconds, then it fades. That is the entire UX.',
    component: ExtV5BackgroundRunMockup,
  },
];

const ExtV5Index = () => {
  const { slug } = useParams();
  const navigate = useNavigate();
  const initialTab = MOCKS.findIndex((m) => m.key === slug);
  const [activeTab, setActiveTab] = useState(initialTab >= 0 ? initialTab : -1);
  useEffect(() => {
    const i = MOCKS.findIndex((m) => m.key === slug);
    setActiveTab(i >= 0 ? i : -1);
  }, [slug]);
  const handleTab = (_, v) => {
    setActiveTab(v);
    if (v === -1) navigate('/mockups/ext-v5');
    else navigate(`/mockups/ext-v5/${MOCKS[v].key}`);
  };

  return (
    <Box sx={{ maxWidth: '100%', mx: 'auto' }}>
      {/* Header */}
      <Box sx={{ mb: 2 }}>
        <Stack direction="row" alignItems="center" spacing={1.5}>
          <ExtensionIcon sx={{ fontSize: 28, color: PURPLE }} />
          <Typography variant="h4" fontWeight={700}>
            Extension v5 — end-user runtime
          </Typography>
          <Chip label="MOCKUP" size="small" color="warning" sx={{ fontWeight: 700 }} />
          <Chip
            label="Silent runner"
            size="small"
            sx={{ fontWeight: 600, bgcolor: '#ede7f6', color: '#4a148c' }}
          />
        </Stack>
        <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
          This is the runtime end-user extension — the one installed on facility
          Chromebooks to replace the current AchieveDXP extension. Users see
          approximately <b>nothing</b>: a toolbar icon, a tiny status popup when
          they click it, and a brief toast when a script runs. <b>All authoring
          lives in the Automatrix web dashboard</b> — not here.
        </Typography>
      </Box>

      <Alert severity="info" sx={{ mb: 2 }}>
        Looking for the script editor, recording mode, or Agent Mode UI?
        Those live in the Automatrix web dashboard, and a prototype
        authoring-in-browser surface is at <code>/mockups/ext-admin</code>.
        Neither is part of the end-user runtime shown here.
      </Alert>

      {/* Tab navigation */}
      <Paper sx={{ mb: 2 }} elevation={1}>
        <Tabs
          value={activeTab}
          onChange={handleTab}
          variant="scrollable"
          scrollButtons="auto"
          sx={{
            '& .MuiTab-root': {
              textTransform: 'none',
              fontWeight: 600,
              fontSize: '0.9rem',
              minHeight: 52,
            },
          }}
        >
          <Tab
            value={-1}
            icon={<ExtensionIcon />}
            iconPosition="start"
            label="Gallery"
          />
          {MOCKS.map((m, idx) => (
            <Tab
              key={m.key}
              value={idx}
              icon={m.icon}
              iconPosition="start"
              label={m.label}
            />
          ))}
        </Tabs>
      </Paper>

      {/* Gallery (cards) */}
      {activeTab === -1 && (
        <Box>
          <Alert severity="success" sx={{ mb: 2 }}>
            All four end-user surfaces. Most users will only ever see the toolbar
            icon and the occasional toast.
          </Alert>
          <Grid container spacing={2}>
            {MOCKS.map((m, idx) => (
              <Grid item xs={12} sm={6} md={3} key={m.key}>
                <Card elevation={2}>
                  <CardActionArea onClick={() => handleTab(null, idx)} sx={{ minHeight: 160 }}>
                    <CardContent>
                      <Stack direction="row" alignItems="center" spacing={1} sx={{ mb: 1 }}>
                        <Box sx={{ color: PURPLE }}>{m.icon}</Box>
                        <Typography variant="subtitle1" fontWeight={700}>
                          {m.title}
                        </Typography>
                      </Stack>
                      <Typography variant="body2" color="text.secondary" sx={{ fontSize: '0.82rem' }}>
                        {m.summary}
                      </Typography>
                      <Typography variant="caption" sx={{
                        display: 'block', mt: 1.5, color: PURPLE, fontFamily: 'monospace', fontSize: '0.7rem',
                      }}>
                        /mockups/ext-v5 · {m.key}
                      </Typography>
                    </CardContent>
                  </CardActionArea>
                </Card>
              </Grid>
            ))}
          </Grid>
        </Box>
      )}

      {/* Individual mock */}
      {activeTab >= 0 && MOCKS[activeTab] && (
        <Box>
          {React.createElement(MOCKS[activeTab].component)}
        </Box>
      )}
    </Box>
  );
};

export default ExtV5Index;
